import * as qs from 'utils/queryString';

import { message } from 'antd';
import { push } from 'connected-react-router';
import { recentOrders as recentOrdersMainLayout, updateItemCart } from 'containers/MainLayout/actions';
import MainActionTypes from 'containers/MainLayout/constants';
import { getStore, getStoreWithRetry } from 'containers/MainLayout/saga';
import { makeSelectLocation, selectCountPendingOrder, selectSuppliers } from 'containers/MainLayout/selectors';
import { all, call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import translations from 'translations';
import { OrderStatus } from 'types/schema';
import {
  approveOrder,
  bulkReorder,
  createReOrder,
  declineOrder,
  downloadOrdersReport,
  getOrders,
  getOrdersOrFulfillmentNotes,
  submitFeedback,
} from 'utils/apollo';
import saveCsv from 'utils/saveCsv';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import messages from '../OrderDetailsPage/messages';
import ordersMessages from '../OrdersPage/messages';
import {
  approveOrder as approveOrderActions,
  bulkReorder as bulkReorderActions,
  disapproveOrder as disapproveOrderActions,
  ordersExportCSV as exportCSVActions,
  fetchTabCounts as fetchTabCountsActions,
  orders as ordersActions,
  ratingAction,
  reOrder as reOrderActions,
  approveOrders as approveOrdersActions,
  declineOrders as declineOrdersActions,
} from './actions';
import ActionTypes from './constants';
import selectOrdersPage from './selectors';

function* initDataFlow() {
  const store = yield call(getStoreWithRetry);
  if (!store) {
    return;
  }

  const location = yield select(makeSelectLocation());
  const defaultSearch = {
    filter: {},
    pagination: {
      page: 0,
      size: 10,
    },
  };

  const parsedSearch = qs.parse(location.search);
  const search = location.search
    ? {
        filter: {
          ...defaultSearch.filter,
          ...parsedSearch.filter,
        },
        pagination: defaultSearch.pagination,
      }
    : defaultSearch;

  const requestParam = computeSearchRequest(search, store.id);

  if (search.filter.supplierName !== '') {
    const { supplierHasOrders } = yield select(selectSuppliers());
    const supplier = supplierHasOrders.find((s: any) => s.name === search.filter.supplierName);
    if (supplier) {
      requestParam.filter.supplierId = supplier.id;
    }
  }

  yield put({
    type: ActionTypes.SET_FILTER,
    payload: search,
  });
  yield put(ordersActions.request(requestParam));
  const response = yield call(getOrdersOrFulfillmentNotes, requestParam);
  if (!response.errors) {
    yield put(ordersActions.success({ orders: response, search: search }));
  } else {
    yield put(ordersActions.failure(response.errors));
  }
}

function* ordersFlow() {
  let skipFirstStoreChange = true;
  yield takeLatest(MainActionTypes.SET_SELECTED_STORE, function* onStoreChange() {
    if (skipFirstStoreChange) {
      skipFirstStoreChange = false;
      return;
    }
    yield call(initDataFlow);
  });
}

function* loadMoreOrdersFlow() {
  const store = yield call(getStoreWithRetry);
  if (!store) {
    return;
  }

  const ordersPage = yield select(selectOrdersPage());
  const { search } = ordersPage;

  const requestParam = computeSearchRequest(search, store.id);

  if (search.filter.supplierName !== '') {
    const { supplierHasOrders } = yield select(selectSuppliers());
    const supplier = supplierHasOrders.find((s: any) => s.name === search.filter.supplierName);
    if (supplier) {
      requestParam.filter.supplierId = supplier.id;
    }
  }

  yield put(ordersActions.request(requestParam));

  const response = yield call(getOrdersOrFulfillmentNotes, requestParam);
  if (!response.errors) {
    yield put(ordersActions.success({ orders: response, search: search }));
  } else {
    yield put(ordersActions.failure(response.errors));
  }
}

const isEmptyObject = (obj) => !obj || !Object.keys(obj).length;

function computeSearchRequest(search, storeId) {
  const {
    filter: { supplierName, orderDateRange, deliveryDateRange, statuses, ...rest },
    pagination: { page, size } = { page: 0, size: 24 },
  } = search;

  const requestParam = {
    filter: {
      storeId: storeId,
      statuses: statuses || [],
      ...rest,
    },
    pagination: {
      page: page,
      size: size,
    },
  };

  if (!isEmptyObject(orderDateRange)) {
    requestParam.filter.orderDateRange = orderDateRange;
  }

  if (!isEmptyObject(deliveryDateRange)) {
    requestParam.filter.deliveryDateRange = deliveryDateRange;
  }

  return requestParam;
}

function* applyFilter() {
  while (true) {
    const { payload } = yield take(ActionTypes.APPLY_FILTER);
    const location = yield select(makeSelectLocation());
    const oldQueryString = location.search.length > 0 ? location.search.slice(1) : '';

    const cleanFilter = Object.keys(payload.filter).reduce((acc, key) => {
      if (payload.filter[key] !== undefined && payload.filter[key] !== null) {
        acc[key] = payload.filter[key];
      }
      return acc;
    }, {});

    const newQueryString = qs.stringify({ filter: cleanFilter });

    if (oldQueryString === newQueryString) {
      continue;
    }

    yield put(
      push({
        search: newQueryString,
      }),
    );

    yield put({
      type: ActionTypes.SET_FILTER,
      payload: {
        ...payload,
        pagination: {
          page: 0,
          size: 10,
        },
      },
    });
  }
}

function* clearFilter() {
  while (true) {
    yield take(ActionTypes.CLEAR_FILTER);
    const store = yield select(selectOrdersPage());
    const location = yield select(makeSelectLocation());

    if (!location.search && isEmptyObject(store.search.filter)) {
      continue;
    }

    yield put(
      push({
        search: '',
      }),
    );

    yield put({
      type: ActionTypes.SET_FILTER,
      payload: {
        filter: {},
        pagination: {
          page: 0,
          size: 10,
        },
      },
    });
  }
}

function* exportCsvSaga() {
  // saveCsv
  while (true) {
    const { payload } = yield take(ActionTypes.EXPORT_CSV);

    const store = yield call(getStore);

    const search = {
      filter: {
        ...payload,
      },
      pagination: {
        page: 0,
        size: 0,
      },
    };

    const requestParam = computeSearchRequest(search, store.id);

    if (search.filter.supplierName !== '') {
      const { supplierHasOrders } = yield select(selectSuppliers());
      const supplier = supplierHasOrders.find((s: any) => s.name === search.filter.supplierName);
      if (supplier) {
        requestParam.filter.supplierId = supplier.id;
      }
    }

    // 'exporting all orders with the current filter. Noted that the export limit is 1000.',

    yield put(
      ordersActions.request({
        filter: requestParam.filter,
        pagination: { page: 0, size: 24 },
      }),
    );

    const response = yield call(downloadOrdersReport, requestParam);
    saveCsv(response.content, 'Exported Orders.csv');
    if (response.errors) {
      yield put(exportCSVActions.failure(response.errors));
    } else {
      yield put(
        exportCSVActions.success({
          response: response,
          search: {
            filter: search.filter,
            pagination: { page: 0, size: 24 },
          },
        }),
      );
    }
  }
}

function* approveOrderFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.APPROVE_ORDER_REQUEST);
    const response = yield call(approveOrder, payload);
    if (!response.errors) {
      const countPending = yield select(selectCountPendingOrder());
      yield put(approveOrderActions.success(response.order));
      yield put(recentOrdersMainLayout.success({ countPendingOrders: countPending - 1 }));
      message.success(translations(messages.approveSuccessfully));
    } else {
      yield put(approveOrderActions.failure(response.errors));
      message.error(response.errors[0].message);
    }
  }
}

function* declineOrderFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.DISAPPROVE_ORDER_REQUEST);
    const response = yield call(declineOrder, payload);
    if (!response.errors) {
      const countPending = yield select(selectCountPendingOrder());
      yield put(recentOrdersMainLayout.success({ countPendingOrders: countPending - 1 }));
      yield put(disapproveOrderActions.success(response.order));
      message.success(translations(messages.disapprovalSuccess));
    } else {
      yield put(disapproveOrderActions.failure(response.errors));
      message.error(translations(messages.declineFailed));
    }
  }
}

function* reOrderFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.REORDER);
    yield put(reOrderActions.request());

    const response = yield call(createReOrder, payload);
    if (!response.errors) {
      yield put(reOrderActions.success(response));
      yield put(updateItemCart.success());
      yield put(push('/checkout'));
    } else {
      yield put(reOrderActions.failure(response.errors));
    }
  }
}

function* bulkReOrderFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.BULK_REORDER);
    yield put(bulkReorderActions.request());

    const response = yield call(bulkReorder, { input: { ...payload } });
    if (!response.errors) {
      yield put(bulkReorderActions.success(response));
      yield put(updateItemCart.success());
      yield put(push('/checkout'));
    } else {
      yield put(bulkReorderActions.failure(response.errors));
    }
  }
}

function* ratingFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.RATE);
    yield put(ratingAction.request());
    const response = yield call(submitFeedback, { input: { ...payload } });
    if (!response.errors) {
      yield put(ratingAction.success({ ...response.feedback, objectId: payload.objectId }));
      if (!payload?.dismissed) {
        message.success(translations(ordersMessages.submitFeedbackSuccessfully));
      }
    } else {
      yield put(ratingAction.failure(response.errors));
      sendGA4Event(EventName.SUBMIT_RATING_ERROR, {
        ffm_id: payload.objectId,
      });
      message.error(response.errors[0].message);
    }
  }
}

function* fetchTabCountsFlow() {
  while (true) {
    yield take(ActionTypes.FETCH_TAB_COUNTS_REQUEST);
    const store = yield call(getStoreWithRetry);
    if (!store) {
      return;
    }

    const ordersPage = yield select(selectOrdersPage());
    const { filter } = ordersPage.search || {};

    const baseFilter: { [key: string]: any } = {
      storeId: store.id,
    };
    if (filter && !isEmptyObject(filter.orderDateRange)) {
      baseFilter.orderDateRange = filter.orderDateRange;
    }

    if (filter && !isEmptyObject(filter.deliveryDateRange)) {
      baseFilter.deliveryDateRange = filter.deliveryDateRange;
    }

    const pendingResponse = yield call(getOrders, {
      filter: {
        ...baseFilter,
        statuses: [OrderStatus.Sent, OrderStatus.Accepted, OrderStatus.InProgress, OrderStatus.PendingApproval],
      },
      pagination: {
        page: 0,
        size: 0,
      },
    });

    const waitingResponse = yield call(getOrders, {
      filter: {
        ...baseFilter,
        statuses: [OrderStatus.PendingDelivery, OrderStatus.Delivering],
      },
      pagination: {
        page: 0,
        size: 0,
      },
    });

    if (!pendingResponse.errors && !waitingResponse.errors) {
      yield put(
        fetchTabCountsActions.success({
          pending: pendingResponse.totalResults || 0,
          waiting: waitingResponse.totalResults || 0,
        }),
      );
    } else {
      yield put(fetchTabCountsActions.failure(pendingResponse.errors || waitingResponse.errors));
    }
  }
}

function* approvalOrdersFlow() {
  while (true) {
    const { payload: orderIds } = yield take(ActionTypes.APPROVE_ORDERS_REQUEST);

    const response = yield all(orderIds.map((orderId) => call(approveOrder, { orderId: orderId })));
    if (!response[0].errors) {
      const countPending = yield select(selectCountPendingOrder());
      yield put(recentOrdersMainLayout.success({ countPendingOrders: countPending - response.length }));
      yield put(approveOrdersActions.success(response));
      message.success(translations(messages.approveSuccessfully));

      const ordersPageState = yield select(selectOrdersPage());
      const store = yield call(getStoreWithRetry);
      const search = ordersPageState.search;
      const requestParam = computeSearchRequest(search, store.id);

      if (search.filter.supplierName !== '') {
        const { supplierHasOrders } = yield select(selectSuppliers());
        const supplier = supplierHasOrders.find((s: any) => s.name === search.filter.supplierName);
        if (supplier) {
          requestParam.filter.supplierId = supplier.id;
        }
      }

      yield put(ordersActions.request(requestParam));
      const ordersResponse = yield call(getOrdersOrFulfillmentNotes, requestParam);
      if (!ordersResponse.errors) {
        yield put(ordersActions.success({ orders: ordersResponse, search: search }));
      }
    } else {
      yield put(approveOrdersActions.failure(response.errors));
      message.error(response[0].errors[0].message);
    }
  }
}

function* declineOrdersFlow() {
  while (true) {
    const { payload: orderIds } = yield take(ActionTypes.DECLINE_ORDERS_REQUEST);

    const response = yield all(orderIds.map((orderId) => call(declineOrder, { orderId: orderId })));

    if (!response[0].errors) {
      const countPending = yield select(selectCountPendingOrder());
      yield put(recentOrdersMainLayout.success({ countPendingOrders: countPending - response.length }));
      yield put(declineOrdersActions.success(response));

      // Refresh orders list
      const ordersPageState = yield select(selectOrdersPage());
      const store = yield call(getStoreWithRetry);
      const search = ordersPageState.search;
      const requestParam = computeSearchRequest(search, store.id);

      if (search.filter.supplierName !== '') {
        const { supplierHasOrders } = yield select(selectSuppliers());
        const supplier = supplierHasOrders.find((s: any) => s.name === search.filter.supplierName);
        if (supplier) {
          requestParam.filter.supplierId = supplier.id;
        }
      }

      yield put(ordersActions.request(requestParam));
      const ordersResponse = yield call(getOrdersOrFulfillmentNotes, requestParam);
      if (!ordersResponse.errors) {
        yield put(ordersActions.success({ orders: ordersResponse, search: search }));
      }
    } else {
      yield put(declineOrdersActions.failure(response.errors));
      message.error(translations(messages.declineFailed));
    }
  }
}

// Individual exports for testing
export default function* ordersPageSaga() {
  // See example in containers/HomePage/saga.js
  yield fork(initDataFlow);
  yield fork(ordersFlow);
  yield takeLatest(ActionTypes.LOAD_MORE_ORDERS, loadMoreOrdersFlow);
  yield fork(applyFilter);
  yield fork(clearFilter);
  yield fork(exportCsvSaga);
  yield fork(approveOrderFlow);
  yield fork(declineOrderFlow);
  yield fork(reOrderFlow);
  yield fork(bulkReOrderFlow);
  yield fork(ratingFlow);
  yield fork(fetchTabCountsFlow);
  yield fork(approvalOrdersFlow);
  yield fork(declineOrdersFlow);
}
